/**
 * Hook de la membresía de un cliente (US-2.4, TG-53).
 *
 * El contrato expone la membresía en singular: `{ active, history }`. El historial
 * es disjunto de la activa (el backend lo garantiza). La red vive en
 * `asignacionesApi`; aquí solo el estado de la vista.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  MembershipAssignment,
  MembershipAssignmentsResponse,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { asignacionesApi } from '../api/asignacionesApi';

const EMPTY: MembershipAssignmentsResponse = { active: null, history: [] };

export interface UseMembershipAssignmentResult {
  active: MembershipAssignment | null;
  history: MembershipAssignment[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la membresía del cliente.';
}

export function useMembershipAssignment(
  customerId: string | undefined,
): UseMembershipAssignmentResult {
  const [data, setData] = useState<MembershipAssignmentsResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!customerId) {
      setData(EMPTY);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    asignacionesApi
      .getMembership(customerId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { active: data.active, history: data.history, loading, error, refresh };
}
